import type { ReactNode } from 'react';
import { memo, useCallback, useMemo, useReducer } from 'react';
import noop from 'lodash/noop';
import { usePathname } from 'next/navigation';
import { createContext, useContextSelector } from 'use-context-selector';
import { useIsQuickSearchV2Enabled } from '@frameio/search/hooks';
import { getSearchOpenedSource } from '@frameio/search/quick-search/utils';
import { KeyboardShortcut } from '@frameio/shared/commands';
import { useTrack } from '@frameio/shared/segment/hooks';

type QuickSearchState = {
  /**
   * @todo (SRCH-241): Deprecate/Remove legacy QuickSearch state
   */
  isModalOpen: boolean;
  toggleModal: () => void;
};

const defaultState: QuickSearchState = {
  isModalOpen: false,
  toggleModal: noop,
};

type QuickSearchAction = { type: 'TOGGLE' };

const quickSearchReducer = (
  state: QuickSearchState,
  action: QuickSearchAction
) => {
  if (action.type === 'TOGGLE') {
    return { ...state, isModalOpen: !state.isModalOpen };
  }

  return state;
};

export const QuickSearchContext = createContext(defaultState);

export const QuickSearchContextProvider = memo(
  ({ children }: { children: ReactNode }) => {
    const { isEnabled: isQuickSearch2Enabled, isLoading } =
      useIsQuickSearchV2Enabled();
    const track = useTrack();
    const pathname = usePathname();
    const [state, dispatch] = useReducer(quickSearchReducer, defaultState);
    const toggleModal = useCallback(() => dispatch({ type: 'TOGGLE' }), []);

    const { isModalOpen } = state;

    const value = useMemo(
      () => ({
        isModalOpen,
        toggleModal,
      }),
      [isModalOpen, toggleModal]
    );

    return (
      <QuickSearchContext.Provider value={value}>
        {!isLoading && !isQuickSearch2Enabled ? (
          <KeyboardShortcut
            id="global.quickSearch"
            onKeyDown={() => {
              track('account-search-opened', {
                source: getSearchOpenedSource(pathname, 'keyboard'),
              });
              toggleModal();
            }}
          />
        ) : null}
        {children}
      </QuickSearchContext.Provider>
    );
  }
);

export type UseQuickSearchModalResults = {
  isModalOpen: boolean;
  toggleModal: () => void;
};

export const useQuickSearchModal = () => {
  const isModalOpen = useContextSelector(
    QuickSearchContext,
    (value) => value?.isModalOpen
  );
  const toggleModal = useContextSelector(
    QuickSearchContext,
    (value) => value?.toggleModal
  );

  return {
    isModalOpen,
    toggleModal,
  };
};
